import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody } from "reactstrap";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import { AppDispatch } from "../../store";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import { resolveApiMessage } from "../../helpers/apiMessage";
import { createTable, deleteTable, getTables, updateTable } from "../../slices/Tables/thunk";
import { getZone } from "../../slices/Zones/thunk";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";

const getTableId = (table: any): string | number | undefined => {
    if (!table || typeof table !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "uuid", "_id", "tableId", "slug"];

    for (const key of candidateKeys) {
        const value = (table as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const getTableNumber = (table: any): string => {
    if (!table || typeof table !== "object") {
        return "Chưa đặt số bàn";
    }

    const candidateKeys = ["number", "name", "label", "title"];

    for (const key of candidateKeys) {
        const value = (table as Record<string, any>)[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }

    if (typeof (table as Record<string, any>).number === "number") {
        return String((table as Record<string, any>).number);
    }

    return "Chưa đặt số bàn";
};

const ZoneTables = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { zoneId } = useParams<{ zoneId: string }>();
    const { tables, loading, error } = useSelector((state: any) => state.Tables);
    const { zones } = useSelector((state: any) => state.Zones);
    const tablesList = useMemo(() => (Array.isArray(tables) ? tables : []), [tables]);
    const resolvedZoneId = useMemo(() => {
        if (!zoneId) {
            return null;
        }
        const parsed = Number(zoneId);
        return Number.isFinite(parsed) ? parsed : null;
    }, [zoneId]);
    const zoneName = useMemo(() => {
        if (!resolvedZoneId || !Array.isArray(zones)) {
            return null;
        }
        const zone = zones.find((item: any) => Number(item?.id ?? item?.zone_id) === resolvedZoneId);
        return zone?.name ?? null;
    }, [zones, resolvedZoneId]);

    const [showAddTableModal, setShowAddTableModal] = useState(false);
    const [newTableNumber, setNewTableNumber] = useState("");
    const [tableNumberError, setTableNumberError] = useState("");
    const TABLE_NUMBER_MAX_LENGTH = 50;

    const openAddTableModal = () => {
        setNewTableNumber("");
        setTableNumberError("");
        setShowAddTableModal(true);
    };

    const closeAddTableModal = () => {
        setShowAddTableModal(false);
        setNewTableNumber("");
        setTableNumberError("");
    };

    const [showDeleteTableModal, setShowDeleteTableModal] = useState(false);
    const [deleteTableId, setDeleteTableId] = useState<string | number | null>(null);
    const [showEditTableModal, setShowEditTableModal] = useState(false);
    const [editTableId, setEditTableId] = useState<string | number | null>(null);
    const [editTableNumber, setEditTableNumber] = useState("");
    const [editTableNumberError, setEditTableNumberError] = useState("");

    useEffect(() => {
        if (!resolvedZoneId) {
            return;
        }
        dispatch(getZone(resolvedZoneId));
        dispatch(getTables({ zone_id: resolvedZoneId }));
    }, [dispatch, resolvedZoneId]);

    const closeEditTableModal = () => {
        setShowEditTableModal(false);
        setEditTableId(null);
        setEditTableNumber("");
        setEditTableNumberError("");
    };

    const openEditTableModal = (table: any) => {
        const identifier = getTableId(table);
        setEditTableId(identifier ?? null);
        setEditTableNumber(getTableNumber(table));
        setEditTableNumberError("");
        setShowEditTableModal(true);
    };

    const handleDeleteTable = async () => {
        if (!deleteTableId) {
            return;
        }

        try {
            const deleteResponse = await dispatch(deleteTable(deleteTableId)).unwrap();
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(deleteResponse, { status: 200 })));
            setDeleteTableId(null);
            dispatch(getTables({ zone_id: resolvedZoneId }));
        } catch (deleteError) {
            dispatch(
                showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(deleteError))
            );
        }
    };

    const handleAddTable = async () => {
        if (!resolvedZoneId) {
            setTableNumberError("Không tìm thấy khu vực.");
            return;
        }
        const trimmedNumber = newTableNumber.trim();

        if (trimmedNumber.length === 0) {
            setTableNumberError("Vui lòng nhập số bàn");
            return;
        }

        if (trimmedNumber.length > TABLE_NUMBER_MAX_LENGTH) {
            setTableNumberError(`Số bàn không được vượt quá ${TABLE_NUMBER_MAX_LENGTH} ký tự`);
            return;
        }

        try {
            const response = await dispatch(createTable({ number: trimmedNumber, zone_id: resolvedZoneId })).unwrap();
            closeAddTableModal();
            dispatch(getTables({ zone_id: resolvedZoneId }));
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 })));
        } catch (createError) {
            setTableNumberError(resolveApiMessage(createError));
            dispatch(
                showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(createError))
            );
        }
    };

    const handleEditTable = async () => {
        if (!editTableId) {
            setEditTableNumberError("Không tìm thấy bàn cần sửa.");
            return;
        }

        const trimmedNumber = editTableNumber.trim();

        if (trimmedNumber.length === 0) {
            setEditTableNumberError("Vui lòng nhập số bàn");
            return;
        }

        if (trimmedNumber.length > TABLE_NUMBER_MAX_LENGTH) {
            setEditTableNumberError(
                `Số bàn không được vượt quá ${TABLE_NUMBER_MAX_LENGTH} ký tự`
            );
            return;
        }

        try {
            const response = await dispatch(updateTable({ tableId: editTableId, data: { number: trimmedNumber } })).unwrap();
            closeEditTableModal();
            dispatch(getTables({ zone_id: resolvedZoneId }));
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 })));
        } catch (updateError) {
            setEditTableNumberError(resolveApiMessage(updateError));
            dispatch(
                showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(updateError))
            );
        }
    };

    const renderTableContent = () => {
        if (!resolvedZoneId) {
            return (
                <div className="cart-table card mb-3">
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="text-center py-3">
                                        Không tìm thấy khu vực.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        if (loading) {
            return (
                <div className="cart-table card mb-3">
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="text-center py-3">
                                        Đang tải danh sách bàn...
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        if (tablesList.length === 0) {
            return (
                <div className="cart-table card mb-3">
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="text-center py-3">
                                        {error ?? "Chưa có bàn nào."}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        return tablesList.map((item: any, index: number) => {
            const tableId = getTableId(item);
            const tableNumber = getTableNumber(item);

            return (
                <div className="cart-table card mb-3" key={tableId ?? index}>
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td>
                                        <span className="product-title">
                                            Bàn {tableNumber}
                                        </span>
                                    </td>
                                    <td className="text-end align-middle">
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm text-white me-1"
                                            onClick={() => {
                                                openEditTableModal(item);
                                            }}
                                        >
                                            <i className="mdi mdi-lead-pencil"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm text-white"
                                            onClick={() => {
                                                setDeleteTableId(tableId ?? null);
                                                setShowDeleteTableModal(true);
                                            }}
                                        >
                                            <i className="mdi mdi-delete"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        });
    };

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={showDeleteTableModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content="Bạn có chắc chắn muốn xóa bàn này không?"
                onConfirm={() => {
                    setShowDeleteTableModal(false);
                    handleDeleteTable();
                }}
                onCancel={() => {
                    setShowDeleteTableModal(false);
                    setDeleteTableId(null);
                }}
            />
            <Modal size="sm" isOpen={showAddTableModal} toggle={closeAddTableModal} centered>
                <ModalBody>
                    <h6 className="fs-15">Vui lòng nhập số bàn</h6>
                    <div className="mb-3">
                        <input
                            className="form-control"
                            type="text"
                            id="table-number"
                            placeholder="VD: 01, 02 ,03, ..."
                            value={newTableNumber}
                            maxLength={TABLE_NUMBER_MAX_LENGTH}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setNewTableNumber(event.target.value);
                                if (tableNumberError) {
                                    setTableNumberError("");
                                }
                            }}
                        />
                        {tableNumberError && (
                            <div className="invalid-feedback d-block">{tableNumberError}</div>
                        )}
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeAddTableModal}>
                        Hủy
                    </Button>
                    <Button color="primary" onClick={handleAddTable}>
                        Thêm
                    </Button>
                </div>
            </Modal>

            <Modal size="sm" isOpen={showEditTableModal} toggle={closeEditTableModal} centered>
                <ModalBody>
                    <h6 className="fs-15">Chỉnh sửa số bàn</h6>
                    <div className="mb-3">
                        <input
                            className="form-control"
                            type="text"
                            placeholder="VD: 01, 02 ,03, ..."
                            value={editTableNumber}
                            maxLength={TABLE_NUMBER_MAX_LENGTH}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setEditTableNumber(event.target.value);
                                if (editTableNumberError) {
                                    setEditTableNumberError("");
                                }
                            }}
                        />
                        {editTableNumberError && (
                            <div className="invalid-feedback d-block">{editTableNumberError}</div>
                        )}
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeEditTableModal}>
                        Hủy
                    </Button>
                    <Button color="primary" onClick={handleEditTable}>
                        Lưu
                    </Button>
                </div>
            </Modal>

            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <Link to="/shop/zones">
                            <i className="mdi mdi-arrow-left"></i>
                        </Link>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">
                            {zoneName ? `Bàn - ${zoneName}` : "Danh sách bàn"}
                        </h6>
                    </div>

                    <div className="d-flex align-items-center">
                        <div
                            className="filter-option"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#suhaFilterOffcanvas"
                            aria-controls="suhaFilterOffcanvas"
                        >
                            <i onClick={openAddTableModal} className="mdi mdi-plus"></i>
                        </div>
                        <HeaderSidebarToggle className="ms-2" />
                    </div>
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">{renderTableContent()}</div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ZoneTables;
